import { kqlEscape } from '../escape-utils'

import { Tool } from './base'
import {
  InlineGroups,
  RenderContext,
  makeRenderer,
  normalizeList,
  scalarToString,
} from './render/render-kit'
import { DatasourceMap } from './render/rtd/datasource.types'
import { AstPredicate } from './render/types'

// Hunt categories CrowdStrike (legacy Falcon) supports for RTD.
// Mirrors the CrowdStrike LogScale `any` set — same telemetry, different query
// language. Legacy Falcon renders bare predicates with no datasource prelude
// or DataDisplay; this map exists so renderSingleSourceGrouped exposes these
// categories and so we can drop generic-leak predicates (process_* etc.) in
// non-core categories that would otherwise duplicate process_creation output.
const CORE_PROCESS_LEAK = [
  'process_path',
  'process_name',
  'process_cmdline',
  'target_path',
  'md5',
  'sha256',
]
const CORE_FILE_LEAK = [
  'process_path',
  'process_name',
  'process_cmdline',
  'target_path',
  'registry_path',
  'registry_key_name',
  'registry_value_name',
]
const REGISTRY_LEAK = [
  'registry_path',
  'registry_key_name',
  'registry_value_name',
]

const crowdStrikeMap: DatasourceMap = {
  CrowdStrike: {
    any: {
      // Core categories — leave open; these are where the generic fields belong.
      process_creation: {},
      file_event: {},
      // Non-core categories — blacklist the acting-process / hash / target-path
      // / registry-path fields that would otherwise duplicate process_creation
      // / file_event output with no prelude to scope them.
      registry_event: { unsupportedFields: CORE_PROCESS_LEAK },
      dns: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      network_connection: {
        unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK],
      },
      image_load: { unsupportedFields: CORE_FILE_LEAK },
      system: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      security: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      authentication_events: {
        unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK],
      },
      pipe_created: { unsupportedFields: CORE_PROCESS_LEAK },
    },
  },
}

export class CrowdStrikeTool extends Tool {
  public readonly toolName = 'CrowdStrike'
  public readonly toolKind = 'edr' as const
  public override readonly datasourceMap = crowdStrikeMap

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}investigate/events/en-US/app/eam2/search?q=${encodedRenderedContent}`
  }

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  /** Implementation Notes
   * Fields
   * - Map via toolTuningFields["crowdstrike"]
   *
   * Values & Lists
   * - Always **double-quoted** (even numbers), escape \ and ".
   * - Multi-values use `IN ("v1","v2",...)`.
   *
   * Logic & Grouping
   * - Uses InlineGroups: **no root parentheses**; nested groups parenthesized.
   * - Logical glue: ` AND ` / ` OR `.
   *
   * Operators
   * - eq / neq: `F = "v"` / `NOT F = "v"`; lists: `F IN (...)` / `NOT F IN (...)`.
   * - contains / doesnotcontain: `F = "*v*"` / `NOT F = "*v*"`.
   * - startswith / endswith: `F = "v*"` / `F = "*v"`.
   * - Comparisons: `F < "<v>"`, `<=`, `>`, `>=` (quoted).
   *
   * Null / Empty
   * - isnull → `NOT F=*`; isnotnull → `F=*`; isempty → `F=""`; isnotempty → `NOT F=""`.
   *
   * Safety
   * - Sanitize identifiers; use centralized quoting/list helpers.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName, // Inputs crowdstrike (splunk) as the tool name in the render-kit config -- this is matched to fieldMaps file
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f, // no eta wrapper
      andGlue: ' AND ', // Capitalization matters, must be all caps
      orGlue: ' OR ', // Capitalization matters, must be all caps
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      /* Null / Empty */
      // static syntax, only pulls in the field name, no value or list is supported
      switch (p.op) {
        case 'isnull':
          return `NOT ${F} = *`
        case 'isnotnull':
          return `${F} = *`
        case 'isempty':
          return `${F} = ""`
        case 'isnotempty':
          return `NOT ${F} = ""`
      }

      // Format all values and check if multi-value from ast
      const rawVals =
        p.value == null
          ? [undefined]
          : Array.isArray(p.value)
            ? p.value
            : [p.value]
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = rawVals.length > 1

      // Format every value's wildcards based on conditional operator
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'contains':
            return `*${s}*`
          case 'doesnotcontain':
            return `*${s}*`
          case 'startswith':
            return `${s}*`
          case 'endswith':
            return `*${s}`
          default:
            return s
        }
      })

      const V = (s: string) => renderContext.quote(s)
      const LIST = () => renderContext.list(shaped)

      /*  All Operators  */

      switch (p.op) {
        case 'eq':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'neq':
          return isMulti
            ? `NOT ${F} IN ${LIST()}`
            : `NOT ${F} = ${V(shaped[0])}`
        case 'contains':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `NOT ${F} IN ${LIST()}`
            : `NOT ${F} = ${V(shaped[0])}`
        case 'startswith':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'endswith':
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
        case 'lt':
          return `${F} < ${V(shaped[0])}`
        case 'lte':
          return `${F} <= ${V(shaped[0])}`
        case 'gt':
          return `${F} > ${V(shaped[0])}`
        case 'gte':
          return `${F} >= ${V(shaped[0])}`
        default:
          return isMulti ? `${F} IN ${LIST()}` : `${F} = ${V(shaped[0])}`
      }
    },
  })

  public readonly tuningText =
    'Insert tuning logic at the end of the first line, after the base logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'
}
